import { createAsyncThunk } from '@reduxjs/toolkit';

import callApi from '../../service/callApi';
import sendRequest from '../../service/sendRequest';
import mapDatasetToDto from '../create-sonar/mapDatasetToDto';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import { clearCrawledUrls } from '../create-sonar/createSonarReducer';
import { CRAWL_PROJECT_STATUS, SONAR_PROGRESS_TYPE } from '../../constants';
import { updateDatasetOptions } from './sonarManagementReducer';
import updateNewSocialsStatuses from '../../utils/updateNewSocialsStatuses';

const GET_DATASET = 'sonar/management/GET_DATASET';
const COLLECT_DATA = 'sonar/management/COLLECT_DATA';
const CRAWL_MISSING = 'sonar/management/CRAWL_MISSING';
const ADD_DATASET = 'sonar/management/ADD_DATASET';
const EDIT_DATASET = 'sonar/management/EDIT_DATASET';
const DELETE_FROM_DATASET = 'sonar/management/DELETE_FROM_DATASET';
const GET_SONAR_PROGRESS = 'sonar/management/GET_SONAR_PROGRESS';
const IMPORT_DATASET = 'sonar/management/IMPORT_DATASET';
const DELETE_SELECTED_ITEMS = 'sonar/management/DELETE_SELECTED_ITEMS';
const CANCEL_PROGRESS = 'sonar/management/CANCEL_PROGRESS';
const GET_DATASET_ITEM = 'sonar/management/GET_DATASET_ITEM';

export const getManagementDataset = createAsyncThunk(GET_DATASET, async (id, { rejectWithValue }) => {
  const response = await sendRequest(
    () =>
      callApi.post(`/api/sonar-data/search/init?sort=-count`, {
        projectId: id,
      }),
    { onError: (e) => rejectWithValue(e) },
  );

  return response.data;
});

export const collectData = createAsyncThunk(COLLECT_DATA, async (id, { dispatch, rejectWithValue }) => {
  dispatch(
    enqueueSnackbar({
      message: 'dataCollectingStarted',
      options: {
        variant: 'warning',
      },
    }),
  );

  sendRequest(() => callApi.get(`/api/jobs/${id}/collect-sonar`), {
    onError: (e) => {
      dispatch(
        enqueueSnackbar({
          message: 'dataCollectingWrong',
          options: {
            variant: 'error',
          },
        }),
      );

      return rejectWithValue(e);
    },
  });

  setTimeout(() => {
    dispatch(getSonarProgress(id));
  }, 500);
});

export const crawlMissingDomains = createAsyncThunk(CRAWL_MISSING, async (id, { rejectWithValue, dispatch }) => {
  sendRequest(() => callApi.get(`/api/jobs/${id}/crawl-init-sonar-data`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  setTimeout(() => {
    dispatch(getSonarProgress(id));
  }, 500);
});

export const getSonarProgress = createAsyncThunk(
  GET_SONAR_PROGRESS,
  async (id, { dispatch, getState, rejectWithValue }) => {
    const crawlData = getState().sonarManagement.sonarProgress[SONAR_PROGRESS_TYPE.CRAWL];
    const collectData = getState().sonarManagement.sonarProgress[SONAR_PROGRESS_TYPE.COLLECT_SONAR];

    const response = await sendRequest(() => callApi.post(`/api/jobs/progress`, { jobEntityId: id }), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    if (response.data.content[0]) {
      const newCrawlData = response.data.content.find(
        (progress) => progress.progressType === SONAR_PROGRESS_TYPE.CRAWL,
      );
      const newCollectSonarData = response.data.content.find(
        (progress) => progress.progressType === SONAR_PROGRESS_TYPE.COLLECT_SONAR,
      );

      if (newCrawlData && crawlData.status && crawlData.handled !== newCrawlData.handled) {
        dispatch(getManagementDataset(id));
      }

      if (
        (collectData.status === CRAWL_PROJECT_STATUS.EXECUTING ||
          collectData.status === CRAWL_PROJECT_STATUS.EXECUTE) &&
        newCollectSonarData.status === CRAWL_PROJECT_STATUS.EXECUTED
      ) {
        dispatch(
          enqueueSnackbar({
            message: 'dataCollectingFinished',
            options: {
              variant: 'success',
            },
          }),
        );
      }

      return response.data.content;
    }

    return [];
  },
);

export const addDataset = createAsyncThunk(
  ADD_DATASET,
  async ({ values, projectId }, { dispatch, rejectWithValue, getState }) => {
    const crawledUrls = getState().createSonar.crawledUrls;

    const datasetDto = mapDatasetToDto(projectId, values, crawledUrls);

    await sendRequest(() => callApi.post(`/api/sonar-data/add `, datasetDto), {
      onError: (e) => {
        return rejectWithValue(e);
      },
      onSuccess: () => {
        dispatch(updateDatasetOptions([datasetDto]));
      },
    });

    dispatch(clearCrawledUrls());
    dispatch(getManagementDataset(projectId));
  },
);

export const deleteFromDataset = createAsyncThunk(DELETE_FROM_DATASET, async (ids, { rejectWithValue }) => {
  await sendRequest(() => callApi.delete(`/api/sonar-data`, { data: ids }), {
    onError: (e) => {
      rejectWithValue(e);
    },
  });

  return ids;
});

export const importDataset = createAsyncThunk(IMPORT_DATASET, async ({ file, id }, { dispatch, rejectWithValue }) => {
  try {
    if (!id) {
      throw new Error();
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await callApi.request({
      url: `/api/sonar-data/init/${id}`,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      data: formData,
    });

    return response.data;
  } catch (e) {
    if (e.response.status === 400) {
      return rejectWithValue(e.response.data);
    } else {
      dispatch(somethingWrongNotification());
      return rejectWithValue({});
    }
  }
});

export const deleteSelectedItems = createAsyncThunk(DELETE_SELECTED_ITEMS, async (_, { dispatch, getState }) => {
  const selectedItems = getState().sonarManagement.dataset.filter(({ isSelected }) => isSelected);
  const selectedItemsIds = selectedItems.map(({ id }) => id);

  dispatch(deleteFromDataset(selectedItemsIds));
});

export const cancelProgress = createAsyncThunk(
  CANCEL_PROGRESS,
  async ({ progressId, jobEntityId }, { dispatch, rejectWithValue }) => {
    if (!progressId) {
      return rejectWithValue(null);
    }

    await sendRequest(() => callApi.get(`/api/jobs/${progressId}/cancel`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    if (jobEntityId) {
      dispatch(getSonarProgress(jobEntityId));
    }
  },
);

export const getDatasetItem = createAsyncThunk(GET_DATASET_ITEM, async (id, { dispatch, rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/sonar-data/${id}`), {
    onError: (e) => {
      dispatch(somethingWrongNotification());
      return rejectWithValue(e);
    },
  });

  dispatch(updateDatasetOptions([response.data]));

  return response.data;
});

export const editDataset = createAsyncThunk(
  EDIT_DATASET,
  async ({ values, datasetId, projectId }, { dispatch, rejectWithValue, getState }) => {
    const crawledUrls = getState().createSonar.crawledUrls;
    const editableDataset = getState().sonarManagement.editableDataset;

    if (!datasetId || !projectId) {
      dispatch(somethingWrongNotification());
      return rejectWithValue(null);
    }

    const datasetDto = mapDatasetToDto(projectId, values, crawledUrls);
    const datasetDtoWithUpdatedSocials = updateNewSocialsStatuses(editableDataset, datasetDto);

    const response = await sendRequest(
      () => callApi.post(`/api/sonar-data/edit?id=${datasetId}`, datasetDtoWithUpdatedSocials),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(null);
        },
        onSuccess: () => {
          dispatch(updateDatasetOptions([datasetDto]));
        },
      },
    );

    dispatch(clearCrawledUrls());

    return response.data;
  },
);
